import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { defaultPresaveServices } from 'fym-shared/src/smartlinks/presave';
import { TypedMemo } from '../../common/types';
import { ReleaseResolution } from '../wizards/common/releaseResolutionTypes';
import { CreationItemCard } from './CreationItemCard';
import { CreationInferenceBox } from './CreationInferenceBox';
import { CreationServiceGrid } from './CreationServiceGrid';
import { CreationSlugField } from './CreationSlugField';
import { CreationThemeRow } from './CreationThemeRow';
import { CreationDateField } from './CreationDateField';
import { CreationPageType } from './inferPageType';
import { CreationDraft } from './props';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sheet; each part uses its own slice of it */
import styles from './SMLCreationSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CreationResolvedStageProps {
  resolution: ReleaseResolution;
  fallbackTitle: string;
  fallbackSubtitle?: string;
  /** The cover of the song (`creationCover.ts`). */
  coverUrl?: string;
  /** One line saying how the sheet reached its conclusion. */
  why: string;
  draft: CreationDraft;
  slugPrefix: string;
  today: string;
}

/**
 * Everything the page will be, on one surface: what it is about, what kind of
 * page that makes it, where it will live and what it will look like.
 */
const CreationResolvedStageComponent: React.FC<CreationResolvedStageProps> = ({
  resolution,
  fallbackTitle,
  fallbackSubtitle,
  coverUrl,
  why,
  draft,
  slugPrefix,
  today,
}) => {
  const intl = useIntl();
  const isPresave = draft.pageType === CreationPageType.presave;
  const foundCount = resolution.foundSources.length;
  const missingCount = resolution.missingSources.length;
  // Services keep arriving on a release page while the search is out. Nothing
  // is missing until it has finished, so the line says so until then.
  const isSearching = !isPresave && resolution.isResolving;
  const { setPageType } = draft;

  // A release page routes somewhere, so it is only on offer once something has
  // resolved. Offering it for a release with no links would put the artist in
  // front of a Create button that can never be pressed.
  const canBeRelease = resolution.main !== undefined;

  const typeOptions = useMemo(
    () => [
      {
        key: CreationPageType.presave,
        label: intl.formatMessage({ id: 'app.sml.create.type.presave.short', defaultMessage: 'Pre-save' }),
        isOn: isPresave,
        onSelect: isPresave ? undefined : () => setPageType(CreationPageType.presave),
      },
      ...(canBeRelease
        ? [
            {
              key: CreationPageType.release,
              label: intl.formatMessage({ id: 'app.sml.create.type.release.short', defaultMessage: 'Release' }),
              isOn: !isPresave,
              onSelect: isPresave ? () => setPageType(CreationPageType.release) : undefined,
            },
          ]
        : []),
    ],
    [canBeRelease, intl, isPresave, setPageType]
  );

  return (
    <>
      <CreationItemCard
        title={resolution.title ?? fallbackTitle}
        subtitle={resolution.artistName ?? fallbackSubtitle}
        source={resolution.sourceLabel}
        imageUrl={coverUrl}
      />

      <CreationInferenceBox type={draft.pageType} why={why} options={typeOptions} />

      {isPresave && (
        <div className={styles.fields}>
          <CreationDateField wide value={draft.releaseDate} min={today} onChange={draft.setReleaseDate} />
        </div>
      )}

      <div>
        <div className={styles.hint}>
          <span className={styles.hintLabel}>
            {intl.formatMessage({ id: 'app.sml.create.services.label', defaultMessage: 'Services' })}
          </span>
          <span className={clsx(styles.hintValue, isSearching && styles.hintBusy)}>
            {/* A spinner on the line itself, so the count reads as a count so
                far rather than the final tally. */}
            {isSearching && <span className={styles.hintSpinner} />}
            {/* A pre-save page does not route anywhere yet, so what matters is
                where a fan can be saved — not where the release was found. */}
            {isPresave &&
              intl.formatMessage(
                {
                  id: 'app.sml.create.services.presave',
                  defaultMessage: '{count} fans can pre-save on',
                },
                { count: defaultPresaveServices.length }
              )}
            {isSearching &&
              intl.formatMessage(
                { id: 'app.sml.create.services.searching', defaultMessage: '{found} found · still looking…' },
                { found: foundCount }
              )}
            {!isPresave &&
              !resolution.isResolving &&
              intl.formatMessage(
                {
                  id: 'app.sml.create.services.summary',
                  defaultMessage: '{found} found · {missing} not there yet',
                },
                { found: foundCount, missing: missingCount }
              )}
          </span>
        </div>
        {isPresave ? (
          <CreationServiceGrid found={defaultPresaveServices} pending={[]} missing={[]} />
        ) : (
          <CreationServiceGrid
            found={resolution.foundSources}
            pending={resolution.pendingSources}
            missing={resolution.missingSources}
          />
        )}
      </div>

      <div className={styles.fields}>
        <CreationSlugField
          slug={draft.slug}
          prefix={slugPrefix}
          platformDomain={draft.platformDomain}
          onPlatformDomainChange={draft.setPlatformDomain}
        />
      </div>

      <CreationThemeRow theme={draft.theme} onChange={draft.setTheme} />
    </>
  );
};

export const CreationResolvedStage = TypedMemo(CreationResolvedStageComponent);
